import { BigNum, PositionDirection } from '@drift-labs/sdk';
import { COMMON_UI_UTILS, ENUM_UTILS } from '@drift/common';
import React from 'react';
import { cm } from 'src/utils/classNames';
import { signDependentValue } from 'src/utils/signDependentValue';

interface Props {
	currentPositionSize: BigNum;
	currentPositionEntryPrice: BigNum;
	currentPositionDirection: 'long' | 'short';
	side: 'buy' | 'sell';
	limitExitSize: BigNum;
	limitExitPrice: BigNum;
	takerFeeBps?: number;
	customLabel?: string;
	showPlusSign?: boolean;
	isMarketOrder?: boolean;
	className?: string;
}

export default function TpSlOrderFormEstimatedPnL({
	currentPositionSize,
	currentPositionEntryPrice,
	currentPositionDirection,
	side,
	limitExitSize,
	limitExitPrice,
	isMarketOrder,
	takerFeeBps,
	className,
	showPlusSign,
}: Props) {
	if (!currentPositionSize) return null;

	let estimatedProfit: BigNum | null = null;
	try {
		const result = COMMON_UI_UTILS.calculatePotentialProfit({
			currentPositionSize: currentPositionSize,
			currentPositionDirection: ENUM_UTILS.toObj(currentPositionDirection),
			tradeDirection:
				side === 'buy' ? PositionDirection.LONG : PositionDirection.SHORT,
			exitBaseSize: limitExitSize,
			exitPrice: limitExitPrice,
			currentPositionEntryPrice: currentPositionEntryPrice,
			isMarketOrder: isMarketOrder,
			takerFeeBps: takerFeeBps || 0,
		});
		estimatedProfit = result.estimatedProfit;
	} catch (error) {
		// Handle precision comparison errors gracefully
		console.warn('Error calculating potential profit:', error);
		return null;
	}

	if (!estimatedProfit) {
		return null;
	}

	return (
		<span
			className={cm(
				'space-x-2 typo-b5',
				signDependentValue(estimatedProfit.toNum(), {
					positive: 'text-text-positive-green-button',
					negative: 'text-text-negative-red-button',
					zero: 'text-text-default',
				}),
				className
			)}
		>
			{signDependentValue(estimatedProfit.toNum(), {
				positive: showPlusSign ? '+' : '',
				negative: '-',
				zero: '',
			})}
			{estimatedProfit.abs().toNotional()}
		</span>
	);
}
